"use client";

import React, { useCallback } from "react";
import { Search, Lightbulb, TrendingUp, Clock } from "lucide-react";
import { Button } from "./button.js";

interface SearchSuggestionsProps {
  recentSearches?: string[];
  popularSearches?: string[];
  onSuggestionClick: (query: string) => void;
  hasResults?: boolean;
  query?: string;
}

export function SearchSuggestions({
  recentSearches = [],
  popularSearches = [],
  onSuggestionClick,
  hasResults = false,
  query = "",
}: SearchSuggestionsProps) {
  const quickSearchTemplates = [
    { label: "Open Tickets", query: "status:open type:ticket" },
    { label: "My Tasks", query: "type:task status:pending" },
    { label: "Recent Contacts", query: "type:contact" },
    { label: "Unread Messages", query: "type:message status:unread" },
  ];

  const searchTips = [
    "Search by ticket number (e.g., #1234)",
    "Search by contact name or email",
    "Use filters to narrow results",
    "Search by date (e.g., 'today', 'this week')",
    "Search by status (e.g., 'open', 'closed')",
  ];

  if (hasResults) {
    return null;
  }

  if (query && !hasResults) {
    // No results state
    return (
      <div className="px-6 py-12 text-center space-y-6">
        <div className="flex justify-center">
          <Search className="h-16 w-16 text-gray-300" />
        </div>
        
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No results found</h3>
          <p className="text-gray-600 mb-4">Try adjusting your search or filters</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
          <h4 className="font-medium text-blue-900 mb-2 flex items-center">
            <Lightbulb className="h-4 w-4 mr-2" />
            Search Tips
          </h4>
          <ul className="space-y-1 text-sm text-blue-800">
            {searchTips.slice(0, 3).map((tip, index) => (
              <li key={index} className="flex items-start">
                <span className="mr-2">•</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>

        {recentSearches.length > 0 && (
          <div>
            <h4 className="font-medium text-gray-900 mb-2 flex items-center justify-center">
              <Clock className="h-4 w-4 mr-2" />
              Try a recent search
            </h4>
            <div className="flex flex-wrap gap-2 justify-center">
              {recentSearches.slice(0, 3).map((search, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => onSuggestionClick(search)}
                  className="text-xs"
                >
                  {search}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Empty state (no query)
  return (
    <div className="px-6 py-12 text-center space-y-8">
      <div className="flex justify-center">
        <Search className="h-16 w-16 text-gray-300" />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Start searching</h3>
        <p className="text-gray-600">Search for tickets, contacts, time entries, and more...</p>
      </div>

      {/* Quick search templates */}
      <div className="space-y-3">
        <h4 className="font-medium text-gray-900 flex items-center justify-center">
          <TrendingUp className="h-4 w-4 mr-2" />
          Quick searches
        </h4>
        <div className="grid grid-cols-2 gap-2">
          {quickSearchTemplates.map((template, index) => (
            <Button
              key={index}
              variant="outline"
              size="sm"
              onClick={() => onSuggestionClick(template.query)}
              className="text-xs"
            >
              {template.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Search tips */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-left max-w-md mx-auto">
        <h4 className="font-medium text-gray-900 mb-3 flex items-center">
          <Lightbulb className="h-4 w-4 mr-2" />
          Search Tips
        </h4>
        <ul className="space-y-2 text-sm text-gray-700">
          {searchTips.map((tip, index) => (
            <li key={index} className="flex items-start">
              <span className="mr-2 text-gray-400">•</span>
              <span>{tip}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Recent searches */}
      {recentSearches.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900 flex items-center justify-center">
            <Clock className="h-4 w-4 mr-2" />
            Recent searches
          </h4>
          <div className="flex flex-wrap gap-2 justify-center">
            {recentSearches.slice(0, 5).map((search, index) => (
              <Button
                key={index}
                variant="secondary"
                size="sm"
                onClick={() => onSuggestionClick(search)}
                className="text-xs"
              >
                {search}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
